type RuntimeBrandConfig = Partial<{
  brandName: string;
  brandTagline: string;
  logoPath: string;
  homePath: string;
  appPath: string;
  privacyPath: string;
  termsPath: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  canonicalUrl: string;
}>;

declare global {
  interface Window {
    __TASKWAY_BRAND__?: RuntimeBrandConfig;
  }
}

const runtimeBrand = globalThis.window?.__TASKWAY_BRAND__ ?? {};

const readEnvValue = (key: string): string | undefined => {
  const env = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env;
  return env?.[key];
};

const readConfigValue = (
  runtimeValue: string | undefined,
  envValue: string | undefined,
  fallback: string,
): string => {
  const prioritized = runtimeValue?.trim() || envValue?.trim() || fallback;
  return prioritized;
};

const brandName = readConfigValue(runtimeBrand.brandName, readEnvValue('REACT_APP_BRAND_NAME'), 'TaskWay');
const brandTagline = readConfigValue(
  runtimeBrand.brandTagline,
  readEnvValue('REACT_APP_BRAND_TAGLINE'),
  'Built for instant staffing and flexible work.',
);
const logoPath = readConfigValue(runtimeBrand.logoPath, readEnvValue('REACT_APP_BRAND_LOGO_PATH'), '/logo.gif');
const homePath = readConfigValue(runtimeBrand.homePath, readEnvValue('REACT_APP_HOME_PATH'), '/home');
const appPath = readConfigValue(runtimeBrand.appPath, readEnvValue('REACT_APP_APP_PATH'), '/app');
const privacyPath = readConfigValue(runtimeBrand.privacyPath, readEnvValue('REACT_APP_PRIVACY_PATH'), '/privacy');
const termsPath = readConfigValue(runtimeBrand.termsPath, readEnvValue('REACT_APP_TERMS_PATH'), '/terms');

const seoTitle = readConfigValue(
  runtimeBrand.seoTitle,
  readEnvValue('REACT_APP_SEO_TITLE'),
  `${brandName} | Instant Local Task Marketplace`,
);
const seoDescription = readConfigValue(
  runtimeBrand.seoDescription,
  readEnvValue('REACT_APP_SEO_DESCRIPTION'),
  `${brandName} connects businesses with trusted local workers in minutes. Post tasks, find flexible jobs, and move faster with smart matching.`,
);
const seoKeywords = readConfigValue(
  runtimeBrand.seoKeywords,
  readEnvValue('REACT_APP_SEO_KEYWORDS'),
  `task marketplace, gig work, local jobs, staffing, instant hiring, ${brandName}`,
);
const canonicalUrl = readConfigValue(
  runtimeBrand.canonicalUrl,
  readEnvValue('REACT_APP_CANONICAL_URL'),
  'https://taskway.app/',
);

export const webAppConfig = Object.freeze({
  brand: Object.freeze({
    name: brandName,
    tagline: brandTagline,
    logoPath,
  }),
  routes: Object.freeze({
    home: homePath,
    app: appPath,
    privacy: privacyPath,
    terms: termsPath,
  }),
  storageKeys: Object.freeze({
    authToken: 'taskway_token',
    theme: 'taskway_theme',
  }),
  seo: Object.freeze({
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
    canonicalUrl,
  }),
});

export type WebAppConfig = typeof webAppConfig;
